import { MessageInstance } from 'antd/es/message/interface'
import { HandleSaveToRolodexType } from 'components/form/formTypes'
import { createAssetComponentData } from 'features/assetDatabase/utils/assetDBAssetsModels'
import {
  getFormFieldsForFeatureType,
  getNewJSONForFeatureType,
} from 'features/assetDatabase/utils/assetDBFeatures'
import {
  checkMissingDerivedLayerFields,
  deleteFeatures,
} from 'features/assetDatabase/utils/assetDBHelpers'
import {
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
  useGeckoAuth,
} from 'features/auth'
import { useRolodexEdit } from 'features/fde'
import { useRolodexCache } from 'features/rolodex'
import React, { createContext, useContext, useMemo, useState } from 'react'
import {
  GeckoAssetModel,
  GeckoFeatureModelType,
  GeckoFeaturesJSON,
  regenerateAssetModel,
  RolodexEntryPutJson,
} from 'types'
import { v4 as uuid4 } from 'uuid'

import { AssetOverviewDataContext } from './AssetOverviewDataLayer'

export interface AssetOverviewUXProps {
  inEditMode: string[]
  setInEditMode: React.Dispatch<React.SetStateAction<string[]>>
  inResolveMode: boolean
  setInResolveMode: React.Dispatch<React.SetStateAction<boolean>>
  hasMissingDerivedLayerFields: boolean[]
  handleSave: (formRef?: React.RefObject<HTMLFormElement>) => void
  handleFeatureAdd: (
    componentID: string,
    featureType: GeckoFeatureModelType
  ) => void
  handleFeatureDelete: (featureToRemove: string) => void
  handleFeatureDuplicate: (featureToDuplicate: string) => void
  handleEdit: (formId: string) => void
  handleCancel: (formId: string) => void
  handleUndo: (formId: string) => void
  handleSaveToRolodex: HandleSaveToRolodexType
  deletedFeatures: string[]
  handleComponentDelete: (
    componentToRemove: string,
    messageApi: MessageInstance,
    forceRefresh: () => Promise<void>
  ) => void

  handleDeleteInRolodex: (
    entryID: string,
    messageApi: MessageInstance,
    forceRefresh: () => Promise<void>
  ) => void
}

//Context used to manage the UX state of the Asset Overview pages
export const AssetOverviewUXLayer: React.FC<{
  children: React.ReactNode
}> = (props) => {
  const { children } = props
  const { assetDataObj, setAssetComponentData } = useContext(
    AssetOverviewDataContext
  )
  const [inResolveMode, setInResolveMode] = useState(false)
  const [deletedFeatures, setDeletedFeatures] = useState<string[]>([])
  const [inEditMode, setInEditMode] = useState<string[]>([])
  const { rolodexPut, rolodexDelete } = useRolodexEdit()
  const { clearAll } = useRolodexCache()
  const { permissions } = useGeckoAuth()

  const hasMissingDerivedLayerFields = useMemo(() => {
    return assetDataObj?.assetModel
      ? checkMissingDerivedLayerFields(assetDataObj)
      : []
  }, [assetDataObj])

  const handleAssetOverviewSave = (
    formRef?: React.RefObject<HTMLFormElement>
  ) => {
    if (formRef && formRef.current) {
      const event = new Event('submit', { bubbles: true, cancelable: true })
      formRef.current.dispatchEvent(event)
    }
  }

  const handleFeatureAdd = async (
    componentID: string,
    featureType: GeckoFeatureModelType
  ) => {
    if (!assetDataObj) return false
    const newFeatureID = uuid4().slice(0, 6)
    const components = assetDataObj.components
    const component = components.find(
      (component) => component.componentData.uniqueID === componentID
    )
    const featureJSON = getNewJSONForFeatureType(featureType)
    const featureFormFields = getFormFieldsForFeatureType(featureJSON)
    component?.features.push({
      uniqueID: newFeatureID,
      featureJSON: featureJSON as GeckoFeaturesJSON,
      formFields: featureFormFields,
    })
    setAssetComponentData([...components])
  }

  const handleFeatureDelete = (featureToRemove: string) => {
    if (!assetDataObj) return false
    const components = assetDataObj.components
    const component = components.find((component) =>
      component.features.find((feature) => feature.uniqueID === featureToRemove)
    )
    if (!component) return false
    const newFeatureArr = component?.features.filter(
      (feature) => feature.uniqueID !== featureToRemove
    )
    component.features = newFeatureArr
    setDeletedFeatures([...deletedFeatures, featureToRemove])
    setAssetComponentData([...components])
  }

  const handleFeatureDuplicate = (featureToDuplicate: string) => {
    if (!assetDataObj) return false
    const components = assetDataObj.components
    const component = components.find((component) =>
      component.features.find(
        (feature) => feature.uniqueID === featureToDuplicate
      )
    )
    if (!component) return false
    const duplicatedFeatureJSON = component?.features.find(
      (feature) => feature.uniqueID === featureToDuplicate
    )?.featureJSON

    if (duplicatedFeatureJSON !== undefined) {
      const featureFormFields = getFormFieldsForFeatureType(
        duplicatedFeatureJSON
      )
      const newFeatureID = uuid4().slice(0, 6)
      component?.features.push({
        uniqueID: newFeatureID,
        featureJSON: duplicatedFeatureJSON as GeckoFeaturesJSON,
        formFields: featureFormFields,
      })
      setAssetComponentData([...components])
    }
  }

  const handleComponentDelete = (
    componentToRemove: string,
    messageApi: MessageInstance,
    forceRefresh: () => Promise<void>
  ) => {
    if (assetDataObj?.assetModel) {
      const assetModelRaw = assetDataObj.assetModel.raw
      const assetModelComponentsJSON = assetModelRaw?.data.components as Record<
        string,
        unknown
      >
      assetModelRaw.data.components = {
        ...deleteFeatures(assetModelComponentsJSON, [componentToRemove]),
      }
      const editedAssetModel = regenerateAssetModel(
        GeckoAssetModel.CreateFromJSON(assetModelRaw)
      )
      handleSaveToRolodex([editedAssetModel], messageApi, forceRefresh)
    }
  }

  const handleAssetOverviewEdit = (formId: string) => {
    setInResolveMode(true)
    if (!inEditMode.includes(formId)) {
      setInEditMode([...inEditMode, formId])
    }
  }

  const handleAssetOverviewCancel = (formId: string) => {
    setInResolveMode(false)
    setInEditMode(inEditMode.filter((layer) => layer !== formId))
  }

  const handleDeleteInRolodex = (
    entryID: string,
    messageApi: MessageInstance,
    forceRefresh: () => Promise<void>
  ) => {
    if (permissions?.has(PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN)) {
      rolodexDelete(entryID)
        .then(() => {
          messageApi.success('Successfully deleted entry in Rolodex!')
        })
        .catch(() => {
          messageApi.error('Failed to delete! Please try again.')
        })
        .finally(() => {
          clearAll()
          forceRefresh()
        })
    } else {
      messageApi.error('You do not have permissions to delete this entry.')
    }
  }

  const handleSaveToRolodex = (
    entries: RolodexEntryPutJson[],
    messageApi: MessageInstance,
    forceRefresh: () => Promise<void>,
    formID?: string
  ) => {
    rolodexPut(entries)
      .then(() => {
        handleAssetOverviewCancel(formID ?? '')
        messageApi.success('Successfully saved entry to Rolodex!')
      })
      .catch(() => {
        messageApi.error('Failed to save entry in Rolodex! Please try again.')
      })
      .finally(() => {
        clearAll()
        forceRefresh()
      })
  }

  const handleUndo = (formId: string) => {
    setInResolveMode(false)
    setInEditMode(inEditMode.filter((layer) => layer !== formId))
    if (assetDataObj?.assetModel) {
      setAssetComponentData(createAssetComponentData(assetDataObj.assetModel))
    }
  }

  return (
    <AssetOverviewUXContext.Provider
      value={{
        inEditMode: inEditMode,
        setInEditMode: setInEditMode,
        inResolveMode,
        setInResolveMode,
        hasMissingDerivedLayerFields,
        handleSave: handleAssetOverviewSave,
        handleFeatureAdd,
        handleFeatureDuplicate,
        handleFeatureDelete,
        handleComponentDelete,
        handleEdit: handleAssetOverviewEdit,
        handleCancel: handleAssetOverviewCancel,
        handleSaveToRolodex: handleSaveToRolodex,
        handleDeleteInRolodex,
        handleUndo,
        deletedFeatures,
      }}>
      {children}
    </AssetOverviewUXContext.Provider>
  )
}

export const AssetOverviewUXContext = createContext<AssetOverviewUXProps>(null!)
